import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Checkbox, Card, Progress, Spin, message } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone, LoginOutlined } from '@ant-design/icons';
import axios from "axios";
import { getAuthToken, isTokenExpired, getUserType, getHeaders } from "../../utility/auth";
import { checkCashierCounterStatus } from "../../api/dayCloseApi";
import CounterClosedBlock from "../../components/CounterClosedBlock";
import "./Login.css";

export default function Login() {
    const [form] = Form.useForm();
    const [showPassword, setShowPassword] = useState(true); // Show password by default
    const [keepLoggedIn, setKeepLoggedIn] = useState(false); // State for "Keep Me Logged In"
    const [isLoggingIn, setIsLoggingIn] = useState(false); // Track login state
    const [isLoadingGeo, setIsLoadingGeo] = useState(false); // Track geolocation loading
    const [loadingMessage, setLoadingMessage] = useState(""); // Loading message
    const [loadingProgress, setLoadingProgress] = useState(0); // Loading progress percentage
    const [counterBlocked, setCounterBlocked] = useState(false); // Track if counter is blocked
    const [blockData, setBlockData] = useState(null); // Store block information
    const navigate = useNavigate();
    const [messageApi, contextHolder] = message.useMessage();

    // Function to get device information
    const getDeviceInfo = () => {
        const userAgent = navigator.userAgent;
        let deviceType = 'Desktop';
        let deviceName = 'Unknown Device';
        let browser = 'Unknown Browser';

        // Detect device type
        if (/mobile/i.test(userAgent)) {
            deviceType = 'Mobile';
        } else if (/tablet|ipad/i.test(userAgent)) {
            deviceType = 'Tablet';
        }

        // Detect device name
        if (/windows/i.test(userAgent)) {
            deviceName = 'Windows PC';
        } else if (/macintosh|mac os x/i.test(userAgent)) {
            deviceName = 'Mac';
        } else if (/linux/i.test(userAgent)) {
            deviceName = 'Linux PC';
        } else if (/android/i.test(userAgent)) {
            deviceName = 'Android Device';
        } else if (/iphone/i.test(userAgent)) {
            deviceName = 'iPhone';
        } else if (/ipad/i.test(userAgent)) {
            deviceName = 'iPad';
        }

        // Detect browser
        if (userAgent.indexOf('Firefox') > -1) {
            browser = 'Mozilla Firefox';
        } else if (userAgent.indexOf('Chrome') > -1 && userAgent.indexOf('Edg') === -1) {
            browser = 'Google Chrome';
        } else if (userAgent.indexOf('Safari') > -1 && userAgent.indexOf('Chrome') === -1) {
            browser = 'Safari';
        } else if (userAgent.indexOf('Edg') > -1) {
            browser = 'Microsoft Edge';
        } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
            browser = 'Opera';
        } else if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident/') > -1) {
            browser = 'Internet Explorer';
        }

        return { deviceType, deviceName, browser };
    };

    // Function to get geolocation
    const getGeolocation = () => {
        return new Promise((resolve) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const latitude = position.coords.latitude;
                        const longitude = position.coords.longitude;
                        
                        resolve({
                            latitude: latitude,
                            longitude: longitude,
                            location_address: `${latitude}, ${longitude}`,
                            map_link: `https://www.google.com/maps?q=${latitude},${longitude}`
                        });
                    },
                    (error) => {
                        console.log('Geolocation error:', error.message);
                        resolve({
                            latitude: null,
                            longitude: null,
                            location_address: null,
                            map_link: null
                        });
                    },
                    {
                        enableHighAccuracy: false,
                        timeout: 10000,
                        maximumAge: 60000
                    }
                );
            } else {
                resolve({
                    latitude: null,
                    longitude: null,
                    location_address: null,
                    map_link: null
                });
            }
        });
    };

    // Function to get IP address
    const getIPAddress = async () => {
        try {
            const response = await fetch('https://api.ipify.org?format=json');
            const data = await response.json();
            return data.ip;
        } catch (error) {
            console.log('Could not fetch IP:', error);
            return null;
        }
    };

    const handleSubmit = async (values) => {
        setIsLoggingIn(true);
        
        console.log('Starting login process...');
        
        const { username, password } = values;
        
        // Get device information
        setIsLoadingGeo(true);
        setLoadingProgress(10);
        setLoadingMessage('Initializing...');
        const { deviceType, deviceName, browser } = getDeviceInfo();
        console.log('Device info:', { deviceType, deviceName, browser });
        
        // Get geolocation
        console.log('Requesting geolocation...');
        setLoadingProgress(20);
        setLoadingMessage('Loading Data...');
        
        // Simulate progress during geolocation request
        const progressInterval = setInterval(() => {
            setLoadingProgress(prev => {
                if (prev < 55) return prev + 5;
                return prev;
            });
        }, 1000);
        
        const geoData = await getGeolocation();
        clearInterval(progressInterval);
        
        console.log('Geolocation data received:', geoData);
        console.log('Latitude:', geoData.latitude, 'Longitude:', geoData.longitude);
        setLoadingProgress(60);
        
        // Get IP address
        setLoadingMessage('Loading Data...');
        console.log('Fetching IP address...');
        setLoadingProgress(75);
        const ipAddress = await getIPAddress();
        console.log('IP address:', ipAddress);
        setLoadingProgress(90);
        setLoadingMessage('Loading Data...');
        setLoadingProgress(100);
        
        // Keep loading visible for a moment to show completion
        await new Promise(resolve => setTimeout(resolve, 500));
        setIsLoadingGeo(false);
        setLoadingProgress(0);
        
        const empdata = { 
            uname: username, 
            pass: password,
            device_name: deviceName,
            device_type: deviceType,
            browser: browser,
            ip_address: ipAddress,
            latitude: geoData.latitude,
            longitude: geoData.longitude,
            location_address: geoData.location_address,
            map_link: geoData.map_link
        };
        
        console.log('Login data being sent:', empdata);
        console.log('Latitude being sent:', empdata.latitude);
        console.log('Longitude being sent:', empdata.longitude);
        console.log('API Base URL:', axios.defaults.baseURL);
        console.log('Full login endpoint:', axios.defaults.baseURL + 'login');

        try {
            console.log('Sending POST request to /login...');
            const res = await axios.post("/login", empdata, getHeaders());
            
            if (res.status === 200) {
                const data = res.data;
                console.log('Login response data:', data); // Debug log
                
                if (data.token) {
                    const token = data.token; // do NOT prepend 'Bearer ' when storing
                    const usertype = data.data.type.toLowerCase(); // Normalize to lowercase
                    const userid = data.data.id || data.data.userid || data.data.empid; // Get user ID
                    const expirationTime = Date.now() + 3 * 60 * 60 * 1000; // 3 hours

                    console.log('Storing user data:', { userid, usertype, uname: data.data.uname }); // Debug log

                    // Check if user is cashier or promoter and counter is already closed
                    if (usertype === "cashier" || usertype === "promoter") {
                        console.log(`Checking counter status for ${usertype}...`);
                        const counterStatus = await checkCashierCounterStatus(data.data.uname || username);
                        
                        if (!counterStatus.canLogin && counterStatus.isClosed) {
                            console.log('Counter is closed, blocking access...');
                            setCounterBlocked(true);
                            setBlockData({
                                cashierName: data.data.uname || username,
                                cashierSummary: counterStatus.cashierSummary,
                                currentBillingDate: counterStatus.currentBillingDate
                            });
                            setIsLoggingIn(false);
                            messageApi.warning('Counter has already been closed for this billing period');
                            return;
                        }
                    }

                    if (keepLoggedIn) {
                        localStorage.setItem("token", token);
                        localStorage.setItem("expirationTime", expirationTime);
                        localStorage.setItem("uname", data.data.uname);
                        localStorage.setItem("usertype", data.data.type); // Store original case
                        localStorage.setItem("userid", userid); // Store user ID
                    } else {
                        sessionStorage.setItem("token", token);
                        sessionStorage.setItem("expirationTime", expirationTime);
                        sessionStorage.setItem("uname", data.data.uname);
                        sessionStorage.setItem("usertype", data.data.type); // Store original case
                        sessionStorage.setItem("userid", userid); // Store user ID
                    }

                    messageApi.success("Logged in Successfully");

                    // Add a small delay to ensure data is stored before navigation
                    setTimeout(() => {
                        // Redirect based on user type
                        if (usertype === "admin") {
                            console.log('Navigating to admin dashboard...');
                            navigate("/dashboard/admin");
                        } else if (usertype === "account") {
                            console.log('Navigating to account dashboard...');
                            navigate("accounts/dashboard");
                        } else if (usertype === "hr") {
                            console.log('Navigating to HR analytics dashboard...');
                            navigate("/hr/analytics-dashboard");
                        } else if (usertype === "modifier") {
                            console.log('Navigating to cancel bill page...');
                            navigate("/bill-management/cancel-bill");
                        } else if (usertype === "cashier") {
                            console.log('Navigating to cashier dashboard...');
                            navigate("/dashboard/cashier");
                        } else if (usertype === "cstoremanager") {
                            console.log('Navigating to club store manager dashboard...');
                            navigate("/dashboard/clubstoremanager");
                        } else if (usertype === "warehousestoremanager" || usertype === "warehouse_store_manager") {
                            console.log('Navigating to warehouse store manager dashboard...');
                            navigate("/dashboard/warehousestoremanager");
                        } else if (usertype === "manager") {
                            console.log('Navigating to manager dashboard...');
                            navigate("/dashboard/manager");
                        } else {
                            console.warn('Unknown user type:', usertype);
                            navigate("/");
                        }
                        setIsLoggingIn(false);
                    }, 200); // Small delay to ensure storage is complete
                } else {
                    messageApi.error("Wrong username/Password");
                    setIsLoggingIn(false);
                }
            } else {
                messageApi.error("Wrong username/Password");
                setIsLoggingIn(false);
            }
        } catch (err) {
            console.error('Login error details:', err);
            console.error('Error response:', err.response);
            console.error('Error status:', err.response?.status);
            console.error('Error data:', err.response?.data);
            
            // More specific error messages
            if (err.response?.status === 401) {
                messageApi.error(err.response?.data?.message || "Invalid username or password");
            } else if (err.response?.status === 500) {
                messageApi.error("Server error. Please try again later.");
            } else if (err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
                messageApi.error("Cannot connect to server. Please check your connection.");
            } else {
                messageApi.error(err.response?.data?.message || "Login failed. Please try again.");
            }
            
            setIsLoggingIn(false);
        }
    };
useEffect(() => {
  // Don't auto-redirect if user is currently logging in
  if (isLoggingIn) {
    return;
  }

  const token = getAuthToken();
  const userType = getUserType()?.toLowerCase(); // Normalize to lowercase for comparison
  console.log("Auto-redirect check - Token:", token);
  console.log("Auto-redirect check - Is expired:", isTokenExpired());
  console.log("Auto-redirect check - User type:", userType);
  console.log("Auto-redirect check - Original user type:", getUserType());

  if (token && !isTokenExpired()) {
    console.log('Auto-redirecting based on existing session...');
    switch (userType) {
      case "admin":
        navigate("/dashboard/admin");
        break;
      case "account":
        navigate("/dashboard/account");
        break;
      case "hr":
        navigate("/hr/analytics-dashboard");
        break;
      case "cashier":
        navigate("/dashboard/cashier");
        break;
      case "cstoremanager":
        navigate("/report/clubstoremanager");
        break;
      
      case "wstoremanager":
        navigate("/dashboard/warehousestoremanager");
        break;
      case "manager":
        navigate("/dashboard/manager");
        break;
      default:
        console.warn('Unknown user type for auto-redirect:', userType);
        navigate("/");
        break;
    }
  }
}, [navigate, isLoggingIn]);

    return (
        <>
            {contextHolder}
            {counterBlocked ? (
                <CounterClosedBlock 
                    cashierName={blockData?.cashierName}
                    cashierSummary={blockData?.cashierSummary}
                    currentBillingDate={blockData?.currentBillingDate}
                />
            ) : (
                <div className="club-login-page">
                {/* Entertainment Icons Background */}
                <div className="entertainment-icons">
                    <i className="fas fa-music icon-float icon-1"></i>
                    <i className="fas fa-cocktail icon-float icon-2"></i>
                    <i className="fas fa-microphone icon-float icon-3"></i>
                    <i className="fas fa-guitar icon-float icon-4"></i>
                    <i className="fas fa-theater-masks icon-float icon-5"></i>
                    <i className="fas fa-dice icon-float icon-6"></i>
                    <i className="fas fa-camera icon-float icon-7"></i>
                    <i className="fas fa-headphones icon-float icon-8"></i>
                    <i className="fas fa-pizza-slice icon-float icon-9"></i>
                    <i className="fas fa-wine-glass icon-float icon-10"></i>
                    <i className="fas fa-drum icon-float icon-11"></i>
                    <i className="fas fa-tv icon-float icon-12"></i>
                    <i className="fas fa-gamepad icon-float icon-13"></i>
                    <i className="fas fa-utensils icon-float icon-14"></i>
                    <i className="fas fa-swimmer icon-float icon-15"></i>
                    <i className="fas fa-birthday-cake icon-float icon-16"></i>
                    <i className="fas fa-fire icon-float icon-17"></i>
                    <i className="fas fa-star icon-float icon-18"></i>
                    <i className="fas fa-heart icon-float icon-19"></i>
                    <i className="fas fa-gift icon-float icon-20"></i>
                </div>

                <div className="login-container">
                    {/* Login Form */}
                    <div className="form-section">
                        <Card 
                            className="form-container"
                            bordered={false}
                            style={{ 
                                maxWidth: 450, 
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '16px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                            }}
                        >
                            <div className="form-header" style={{ textAlign: 'center', marginBottom: 32 }}>
                                <div className="logo-icon" style={{ 
                                    fontSize: 48, 
                                    color: '#1890ff',
                                    marginBottom: 16 
                                }}>
                                    <i className="fas fa-cocktail"></i>
                                </div>
                                <h2 style={{ 
                                    fontSize: 28, 
                                    fontWeight: 600, 
                                    margin: 0,
                                    color: '#1890ff'
                                }}>Jannaat Club</h2>
                                <p style={{ 
                                    color: '#666', 
                                    fontSize: 14,
                                    margin: '8px 0 0 0'
                                }}>Entertainment Management System</p>
                            </div>
                            
                            <Form
                                form={form}
                                name="login"
                                onFinish={handleSubmit}
                                layout="vertical"
                                size="large"
                            >
                                <Form.Item
                                    name="username"
                                    rules={[{ required: true, message: 'Please input your username!' }]}
                                >
                                    <Input
                                        prefix={<UserOutlined />}
                                        placeholder="Username"
                                        autoComplete="username"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="password"
                                    rules={[{ required: true, message: 'Please input your password!' }]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined />}
                                        placeholder="Password"
                                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                        visibilityToggle={true}
                                        autoComplete="current-password"
                                    />
                                </Form.Item>

                                <Form.Item>
                                    <Checkbox 
                                        checked={keepLoggedIn}
                                        onChange={(e) => setKeepLoggedIn(e.target.checked)}
                                    >
                                        Keep me logged in
                                    </Checkbox>
                                </Form.Item>

                                <Form.Item>
                                    <Button 
                                        type="primary" 
                                        htmlType="submit" 
                                        block
                                        loading={isLoggingIn}
                                        icon={<LoginOutlined />}
                                        style={{
                                            height: 48,
                                            fontSize: 16,
                                            fontWeight: 500
                                        }}
                                    >
                                        Sign In to Dashboard
                                    </Button>
                                </Form.Item>
                            </Form>
                            
                            {/* Loading Progress */}
                            {isLoadingGeo && (
                                <div style={{ 
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'rgba(255, 255, 255, 0.95)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '16px',
                                    zIndex: 1000
                                }}>
                                    <Spin size="large" />
                                    <div style={{ marginTop: 16, fontSize: 16, color: '#666' }}>
                                        {loadingMessage}
                                    </div>
                                    <div style={{ marginTop: 8, fontSize: 24, fontWeight: 600, color: '#1890ff' }}>
                                        {loadingProgress}%
                                    </div>
                                    <Progress 
                                        percent={loadingProgress} 
                                        showInfo={false}
                                        style={{ width: 300, marginTop: 16 }}
                                    />
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
                </div>
            )}
        </>
    );
}

